import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listingsApi } from '../api/listings';
import { vermieterApi } from '../api/vermieter';
import { verfahrenApi } from '../api/verfahren';
import { gruppeApi } from '../api/gruppe';
import { LISTING_STATES } from '../types';
import ConfirmDeleteModal from './ConfirmDeleteModal';

type Popover = 'status' | 'vermieter' | 'verfahren' | 'gruppe' | null;

// Explicit class maps so Tailwind's content scanner keeps these classes in the bundle.
const STATE_BG: Record<string, string> = {
  blue:   'bg-blue-100 text-blue-800',
  cyan:   'bg-cyan-100 text-cyan-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  purple: 'bg-purple-100 text-purple-800',
  green:  'bg-green-100 text-green-800',
  gray:   'bg-gray-100 text-gray-700',
};
const STATE_RING: Record<string, string> = {
  blue:   'ring-blue-500',
  cyan:   'ring-cyan-500',
  yellow: 'ring-yellow-500',
  purple: 'ring-purple-500',
  green:  'ring-green-500',
  gray:   'ring-gray-400',
};

interface MapBulkActionBarProps {
  selectedIds: Set<number>;
  onClear: () => void;
  /** Invoked after a successful mutation so the page can invalidate queries and reset selection. */
  onMutationSuccess: () => void;
}

export default function MapBulkActionBar({ selectedIds, onClear, onMutationSuccess }: MapBulkActionBarProps) {
  const queryClient = useQueryClient();
  const [popover, setPopover] = useState<Popover>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Close popover when selection becomes empty
  useEffect(() => {
    if (selectedIds.size === 0) setPopover(null);
  }, [selectedIds.size]);

  const ids = [...selectedIds];
  const count = ids.length;

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['listings'] });
    setPopover(null);
    onMutationSuccess();
  };

  const handleGruppeSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['gruppen'] });
    queryClient.invalidateQueries({ queryKey: ['gruppe'] });
    setPopover(null);
    onMutationSuccess();
  };

  if (count === 0) return null;

  return (
    <>
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-[500] bg-white rounded-xl shadow-lg border border-gray-200 px-4 py-2.5 flex items-center gap-3">
        <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
          {count} {count === 1 ? 'Inserat' : 'Inserate'} ausgewählt
        </span>

        <div className="h-5 w-px bg-gray-200" />

        <div className="relative">
          <BarButton active={popover === 'status'} onClick={() => setPopover(p => p === 'status' ? null : 'status')}>
            Status ändern
          </BarButton>
          {popover === 'status' && (
            <StatusPopover ids={ids} onSuccess={handleSuccess} onClose={() => setPopover(null)} />
          )}
        </div>

        <div className="relative">
          <BarButton active={popover === 'vermieter'} onClick={() => setPopover(p => p === 'vermieter' ? null : 'vermieter')}>
            Vermieter zuweisen
          </BarButton>
          {popover === 'vermieter' && (
            <SearchAssignPopover
              ids={ids}
              field="vermieter_id"
              placeholder="Vermieter suchen…"
              entityLabel="Vermieter"
              search={async (q) => {
                const res = await vermieterApi.getVermieter({ search_text: q, per_page: 10 });
                return res.items.map(v => ({ id: v.id, label: v.name, sublabel: v.typ ?? undefined }));
              }}
              onSuccess={handleSuccess}
              onClose={() => setPopover(null)}
            />
          )}
        </div>

        <div className="relative">
          <BarButton active={popover === 'verfahren'} onClick={() => setPopover(p => p === 'verfahren' ? null : 'verfahren')}>
            Verfahren zuweisen
          </BarButton>
          {popover === 'verfahren' && (
            <SearchAssignPopover
              ids={ids}
              field="verfahren_id"
              placeholder="Aktenzeichen oder Stichwort…"
              entityLabel="Verfahren"
              search={async (q) => {
                const res = await verfahrenApi.getVerfahren({ search_text: q, per_page: 10 });
                return res.items.map(v => ({
                  id: v.id,
                  label: v.aktenzeichen || `Verfahren #${v.id}`,
                  sublabel: v.staat,
                }));
              }}
              onSuccess={handleSuccess}
              onClose={() => setPopover(null)}
            />
          )}
        </div>

        <div className="relative">
          <BarButton active={popover === 'gruppe'} onClick={() => setPopover(p => p === 'gruppe' ? null : 'gruppe')}>
            Zu Gruppe hinzufügen
          </BarButton>
          {popover === 'gruppe' && (
            <GruppePopover ids={ids} onSuccess={handleGruppeSuccess} onClose={() => setPopover(null)} />
          )}
        </div>

        <button
          onClick={() => setConfirmDeleteOpen(true)}
          className="text-sm font-medium px-3 py-1.5 rounded-md text-red-700 hover:bg-red-50 transition-colors"
        >
          Ausblenden
        </button>

        <div className="h-5 w-px bg-gray-200" />

        <button
          onClick={onClear}
          className="text-sm text-gray-500 hover:text-gray-700 px-2"
          title="Auswahl aufheben (Esc)"
        >
          ✕
        </button>
      </div>

      <ConfirmDeleteModal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        entity="inserat"
        ids={ids}
        onSuccess={() => {
          setConfirmDeleteOpen(false);
          handleSuccess();
        }}
      />
    </>
  );
}

function BarButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`text-sm font-medium px-3 py-1.5 rounded-md transition-colors ${
        active ? 'bg-blue-100 text-blue-800' : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );
}

// ── Status popover ────────────────────────────────────────────────────────────

function StatusPopover({ ids, onSuccess, onClose }: { ids: number[]; onSuccess: () => void; onClose: () => void }) {
  const [state, setState] = useState('');
  const [comment, setComment] = useState('');
  const ref = useOnOutsideClick<HTMLDivElement>(onClose);

  const mutation = useMutation({
    mutationFn: () => listingsApi.bulkChangeState(ids, state, comment || undefined),
    onSuccess,
  });

  return (
    <PopoverShell innerRef={ref}>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Neuer Status</div>
      <div className="flex flex-col gap-1.5 mb-3">
        {Object.entries(LISTING_STATES).map(([key, info]) => {
          const isSelected = state === key;
          return (
            <button
              key={key}
              onClick={() => setState(key)}
              className={`text-left text-xs px-2.5 py-1.5 rounded-md transition-colors ${
                isSelected ? `${STATE_BG[info.color] ?? STATE_BG.gray} ring-2 ring-offset-1 ${STATE_RING[info.color] ?? STATE_RING.gray}` : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              {info.label}
            </button>
          );
        })}
      </div>
      <textarea
        value={comment}
        onChange={e => setComment(e.target.value)}
        placeholder="Kommentar (optional)"
        rows={2}
        className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 mb-2"
      />
      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="text-xs text-gray-600 px-3 py-1.5 hover:text-gray-800"
        >
          Abbrechen
        </button>
        <button
          onClick={() => mutation.mutate()}
          disabled={!state || mutation.isPending}
          className="text-xs font-medium bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {mutation.isPending ? '…' : 'Anwenden'}
        </button>
      </div>
    </PopoverShell>
  );
}

// ── Search + assign popover (Vermieter & Verfahren) ───────────────────────────

interface SearchHit { id: number; label: string; sublabel?: string }

function SearchAssignPopover({
  ids, field, placeholder, entityLabel, search, onSuccess, onClose,
}: {
  ids: number[];
  field: 'vermieter_id' | 'verfahren_id';
  placeholder: string;
  entityLabel: string;
  search: (query: string) => Promise<SearchHit[]>;
  onSuccess: () => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const ref = useOnOutsideClick<HTMLDivElement>(onClose);

  const { data: hits = [], isFetching } = useQuery({
    queryKey: [field, 'bulk-search', query],
    queryFn: () => search(query),
    enabled: query.trim().length >= 1,
  });

  const mutation = useMutation({
    mutationFn: (targetId: number | null) => listingsApi.bulkUpdate(ids, field, targetId),
    onSuccess,
  });

  return (
    <PopoverShell innerRef={ref}>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{entityLabel} zuweisen</div>
      <input
        autoFocus
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder={placeholder}
        className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 mb-2"
      />
      <div className="max-h-48 overflow-y-auto -mx-1">
        {query.trim().length === 0 ? (
          <div className="text-xs text-gray-400 px-2 py-3 text-center">Tippe, um zu suchen.</div>
        ) : isFetching ? (
          <div className="text-xs text-gray-400 px-2 py-3 text-center">Suche…</div>
        ) : hits.length === 0 ? (
          <div className="text-xs text-gray-400 px-2 py-3 text-center">Keine Treffer.</div>
        ) : (
          hits.map(hit => (
            <button
              key={hit.id}
              onClick={() => mutation.mutate(hit.id)}
              disabled={mutation.isPending}
              className="w-full text-left px-2 py-1.5 rounded-md hover:bg-blue-50 text-sm disabled:opacity-50"
            >
              <div className="font-medium text-gray-800 truncate">{hit.label}</div>
              {hit.sublabel && <div className="text-[11px] text-gray-500 truncate">{hit.sublabel}</div>}
            </button>
          ))
        )}
      </div>
      <div className="border-t border-gray-100 mt-2 pt-2 flex justify-between">
        <button
          onClick={() => mutation.mutate(null)}
          disabled={mutation.isPending}
          className="text-xs text-gray-600 hover:text-red-700 disabled:opacity-50"
        >
          {entityLabel} entfernen
        </button>
        <button
          onClick={onClose}
          className="text-xs text-gray-600 hover:text-gray-800"
        >
          Abbrechen
        </button>
      </div>
    </PopoverShell>
  );
}

// ── Gruppe popover (bestehende Gruppe suchen ODER neue anlegen) ────────────────

function GruppePopover({ ids, onSuccess, onClose }: { ids: number[]; onSuccess: () => void; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [newName, setNewName] = useState('');
  const ref = useOnOutsideClick<HTMLDivElement>(onClose);

  const { data: hits = [], isFetching } = useQuery({
    queryKey: ['gruppen', 'bulk-search', query],
    queryFn: async () => {
      const res = await gruppeApi.getGruppen({ search_text: query || undefined, per_page: 10 });
      return res.items;
    },
    enabled: query.trim().length >= 1,
  });

  const addMutation = useMutation({
    mutationFn: (gruppeId: number) => gruppeApi.addListings(gruppeId, ids),
    onSuccess,
  });

  const createMutation = useMutation({
    mutationFn: () => gruppeApi.createGruppe({ name: newName.trim(), listing_ids: ids }),
    onSuccess,
  });

  return (
    <PopoverShell innerRef={ref}>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        {ids.length} {ids.length === 1 ? 'Inserat' : 'Inserate'} zu Gruppe hinzufügen
      </div>
      <input
        autoFocus
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Bestehende Gruppe suchen…"
        className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 mb-2"
      />
      <div className="max-h-40 overflow-y-auto -mx-1">
        {query.trim().length === 0 ? (
          <div className="text-xs text-gray-400 px-2 py-3 text-center">Tippe, um zu suchen.</div>
        ) : isFetching ? (
          <div className="text-xs text-gray-400 px-2 py-3 text-center">Suche…</div>
        ) : hits.length === 0 ? (
          <div className="text-xs text-gray-400 px-2 py-3 text-center">Keine Gruppe gefunden.</div>
        ) : (
          hits.map(g => (
            <button
              key={g.id}
              onClick={() => addMutation.mutate(g.id)}
              disabled={addMutation.isPending}
              className="w-full text-left px-2 py-1.5 rounded-md hover:bg-blue-50 text-sm disabled:opacity-50"
            >
              <div className="font-medium text-gray-800 truncate">{g.name}</div>
              <div className="text-[11px] text-gray-500 truncate">{g.inserate_count} Inserate</div>
            </button>
          ))
        )}
      </div>
      <div className="border-t border-gray-100 mt-2 pt-2">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Neue Gruppe</div>
        <div className="flex gap-2">
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Name der Gruppe"
            className="flex-1 text-sm border border-gray-200 rounded-md px-2 py-1.5"
          />
          <button
            onClick={() => createMutation.mutate()}
            disabled={!newName.trim() || createMutation.isPending}
            className="text-xs font-medium bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
          >
            {createMutation.isPending ? '…' : 'Anlegen'}
          </button>
        </div>
      </div>
    </PopoverShell>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function PopoverShell({ innerRef, children }: { innerRef: React.RefObject<HTMLDivElement | null>; children: React.ReactNode }) {
  return (
    <div
      ref={innerRef}
      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 bg-white border border-gray-200 rounded-xl shadow-xl p-3"
      onClick={e => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

function useOnOutsideClick<T extends HTMLElement>(onClose: () => void) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    // Slight delay so the open-click does not immediately close the popover
    const id = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handler);
    };
  }, [onClose]);
  return ref;
}
